import React, { useState } from "react";
import { getImageUrl } from "utils/imageHelper";
import { FiFileText, FiEye, FiExternalLink } from "react-icons/fi";
import Modal from "components/common/Modal";

export default function ViewSpeakerDetails({ data }) {
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  if (!data) return null;

  const imageUrl = data.profileImage ? getImageUrl(data.profileImage) : null;
  const pdfUrl = data.speakerDoc ? getImageUrl(data.speakerDoc) : null;

  return (
    <div className="flex flex-col gap-6 text-sm text-gray-800">
      {/* SPEAKER DETAILS */}
      <div className="view-card flex flex-col gap-6">
        <div className="view-subtitle">
          <span>Speaker Details</span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="view-label">Full Name</p>
            <p className="view-value font-semibold">{data.name || "N/A"}</p>
          </div>

          <div>
            <p className="view-label">Email</p>
            <p className="view-value">{data.email || "N/A"}</p>
          </div>

          <div>
            <p className="view-label">Profession</p>
            <p className="view-value">{data.profession || "N/A"}</p>
          </div>

          <div>
            <p className="view-label">Website</p>
                <p className="view-value">
            {data.website ? (
              <a
                href={data.website}
                target="_blank"
                rel="noopener noreferrer"
                className=" text-sky-600 hover:underline"
              >
                {data.website}
              </a>
            ) : (
              "N/A"
            )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1">
          <div>
            <p className="view-label">Description</p>
            <p className="view-value">{data.description || "N/A"}</p>
          </div>
        </div>
      </div>

      {/* SOCIAL & CONTACT LINKS */}
      <div className="view-card flex flex-col gap-6">
        <div className="view-subtitle">
          <span>Social & Contact Links</span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="view-label">Facebook Profile</p>
            <p className="view-value">
              {data.facebookLink ? (
                <a
                  href={data.facebookLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className=" text-sky-600 hover:underline"
                >
                  {data.facebookLink}
                </a>
              ) : (
                "N/A"
              )}
            </p>
          </div>

          <div>
            <p className="view-label">X (Twitter) Profile</p>
            <p className="view-value">
              {data.xLink ? (
                <a
                  href={data.xLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className=" text-sky-600 hover:underline"
                >
                  {data.xLink}
                </a>
              ) : (
                "N/A"
              )}
            </p>
          </div>

          <div>
            <p className="view-label">LinkedIn Profile</p>
                <p className="view-value">
            {data.linkedinLink ? (
              <a
                href={data.linkedinLink}
                target="_blank"
                rel="noopener noreferrer"
                className=" text-sky-600 hover:underline"
              >
                {data.linkedinLink}
              </a>
            ) : (
              "N/A"
            )}
            </p>
          </div>

          <div>
            <p className="view-label">WhatsApp Number</p>
            <p className="view-value">{data.whatsappNumber || "N/A"}</p>
          </div>
        </div>
      </div>

      {/* SPEAKER MEDIA */}
      <div className="view-card flex flex-col gap-6">
        <div className="view-subtitle">
          <span>Speaker Media</span>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* PROFILE IMAGE */}
          <div>
            <p className="view-label mb-2">Profile Image</p>
            <div className="flex h-36 w-36 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-[var(--color-primary,#02a9e0)] bg-gray-50 p-2">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={data.name || "Speaker Profile"}
                  className="h-full w-full rounded-lg object-cover"
                />
              ) : (
                <span className="text-xs text-gray-400">No Image Uploaded</span>
              )}
            </div>
          </div>

          {/* SPEAKER DOCUMENT (PDF) */}
          <div>
            <p className="view-label mb-2">Speaker Document (PDF)</p>
            {pdfUrl ? (
              <div className="flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 text-red-600">
                    <FiFileText className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-800">
                      Speaker Document.pdf
                    </p>
                    <p className="text-[11px] text-gray-400">PDF Document</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsPdfModalOpen(true)}
                    className="shadow-xs flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <FiEye /> Modal View
                  </button>

                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-sky-600 shadow-xs hover:bg-sky-700 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
                  >
                    <FiExternalLink /> Open Page
                  </a>
                </div>
              </div>
            ) : (
              <p className="view-value text-gray-400">No Document Uploaded</p>
            )}
          </div>
        </div>
      </div>

      {/* PDF MODAL */}
      {pdfUrl && (
        <Modal
          open={isPdfModalOpen}
          onClose={() => setIsPdfModalOpen(false)}
          title="Speaker Document Preview"
          maxWidth="max-w-5xl"
        >
          <div className="h-[75vh] w-full overflow-hidden rounded-xl bg-gray-100">
            <iframe
              src={pdfUrl}
              title="PDF Document Viewer"
              className="h-full w-full border-none"
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
